<template>
  <div class="review">
    <div class="d-flex items-center">
      <v-avatar
        :color="isAnonymized ? 'transparent' : 'indigo'"
        size="36"
        class="mr-6"
      >
        <img
          v-if="!isAnonymized"
          :src="item.reviewer.profilePhotoUrl"
          :alt="item.reviewer.displayName"
        />
        <img
          v-else
          src="@/static/avatars/av.png"
          :alt="$t('message.reviews.userDataHidden')"
        />
      </v-avatar>
      <div class="relative border-right d-flex flex-column flex-fill pr-4">
        <div class="d-flex justify-space-between align-center review__name">
          <template v-if="!isAnonymized">
            {{ item.reviewer.displayName }}
          </template>
          <template v-else>
            {{ $t("message.reviews.userDataHidden") }}
          </template>
        </div>

        <div class="d-flex justify-start align-center mb-3 -ml-2 relative">
          <StarsRating :rating="item.rating" simple />
          <p class="mb-0 ml-2 grey--text text--lighten-1 review__date">
            {{
              `${$t("message.reviews.addedLabel")}: ${parseDate(
                item.gmb_create_time
              )}`
            }}
            <template v-if="isCreateAndUpdateDateDifferent">
              ({{
                `${$t("message.common.edited")}: ${parseDate(
                  item.gmb_update_time
                )}`
              }})
            </template>
          </p>
        </div>

        <div
          class="review__comment"
          :class="{
            'review__comment--line': item.reply_comment,
            'review__comment--missing': !item.comment
          }"
        >
          <v-icon v-if="!item.comment" class="review__comment-icon">
            mdi-information
          </v-icon>
          <span v-html="`${itemContent}${textEllipsis}`"></span>
          <span
            @click="showMore = true"
            class="font-weight-bold cursor-pointer"
            v-if="item.comment && item.comment.length > 190 && !showMore"
          >
            {{ $t("message.reviews.more") }}
          </span>
        </div>
        <p class="review__reply--title">
          {{ $t("message.reviews.suggestedAnswerByAI") }}:
        </p>
        <div class="review__reply">
          {{ item.auto_reply_text }}
        </div>
        <div class="reply-actions">
          <ui-button
            color="pink"
            outlined
            icon="icon-trash"
            @click="reject"
            :loading="isRejecting"
          >
            {{ $t("message.common.reject") }}
          </ui-button>
          <ui-button color="primary" @click="accept" :loading="isAccepting">
            {{ $t("message.reviews.publishAnswer") }}
          </ui-button>
        </div>
      </div>
      <div class="reply-info">
        <div class="d-flex justify-start align-center">
          <v-icon class="mr-3" color="primary">icon-owner</v-icon>
          <div>
            <p class="mb-0 gmb-name">
              {{ item.gmb_address_name }}
            </p>
            <p class="mb-0 gmb-address">
              {{ storeCode }}
            </p>
          </div>
        </div>
        <slot name="tags"> </slot>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import StarsRating from "@/components/ui/StarsRating";
import { permissionsInfo } from "@/helpers/mixins/permissions-info";
import serviceHandler from "@/helpers/handlers/service";
import autoReplies from "@/services/autoReplies";

export default {
  name: "AutoReplyReviewItem",
  components: {
    StarsRating
  },
  mixins: [permissionsInfo],
  props: {
    item: {
      required: true,
      type: Object
    }
  },
  data() {
    return {
      showMore: false,
      isAccepting: false,
      isRejecting: false
    };
  },
  computed: {
    isCreateAndUpdateDateDifferent() {
      return (
        this.parseDate(this.item.gmb_create_time) !==
        this.parseDate(this.item.gmb_update_time)
      );
    },
    isAnonymized() {
      return this.item.disabled_collecting_personal_data;
    },
    itemContent() {
      return this.item.comment
        ? this.item.comment.substring(
            0,
            !this.showMore ? 190 : this.item.comment.length
          )
        : this.$t("message.reviews.noComment");
    },
    textEllipsis() {
      return this.item.comment &&
        this.item.comment.length > 190 &&
        !this.showMore
        ? "..."
        : "";
    },
    storeCode() {
      const storeLabel =
        "store_code" in this.item && this.item.store_code
          ? ` | ${this.$t("message.common.storeCode")}: ${this.item.store_code}`
          : "";
      return `${this.item.formatted_address}${storeLabel}`;
    },
    payload() {
      return {
        auto_reply_ids: [this.item.auto_reply_id]
      };
    }
  },
  methods: {
    parseDate(date) {
      return moment(date).format("DD.MM.YYYY");
    },
    async accept() {
      this.isAccepting = true;
      try {
        await serviceHandler(autoReplies.acceptReply(this.payload));
        this.$notify({
          group: "default",
          type: "success",
          text: this.$t("message.reviews.replyPublished")
        });
      } catch (err) {
        this.$notify({
          group: "default",
          type: "error",
          text: this.$t("message.error.other")
        });
        this.$emit("action-error");
      }

      this.isAccepting = false;
      this.$emit("action-success");
    },
    async reject() {
      this.isRejecting = true;
      try {
        await serviceHandler(autoReplies.rejectReply(this.payload));
        this.$notify({
          group: "default",
          type: "success",
          text: this.$t("message.reviews.replyRejected")
        });
      } catch (err) {
        this.$notify({
          group: "default",
          type: "error",
          text: this.$t("message.error.other")
        });
        this.$emit("action-error");
      }
      this.isRejecting = false;
      this.$emit("action-success");
    }
  }
};
</script>

<style lang="scss" scoped>
.review-actions {
  display: flex;
  gap: $spacing * 2;
  align-items: center;

  &__icon {
    cursor: pointer;
    position: relative;
  }

  &__alert {
    width: 6px;
    height: 6px;
    position: absolute;
    z-index: 1;
    top: $spacing * 0.2;
    right: $spacing * 0.1;
    background: $pink;
    border-radius: 100%;
  }
}
.gmb-name {
  font-weight: 600;
  @include font-size(12px);
  line-height: 16px;
  color: $dark-blue-3;
}

.gmb-address {
  @include font-size(12px);
  line-height: 16px;
  font-weight: normal;
  color: $dark-blue-3;
}

.absolute-avatar {
  position: absolute;
  left: -60px;

  &--disabled {
    &::after {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: $light;
      opacity: 0.5;
    }
  }
}

.relative {
  position: relative;
}

.review {
  position: relative;
  padding: $spacing * 2.5 0;
  border-bottom: 1px solid $base-light-2;
  margin: 0;

  .border-right {
    border-right: 1px solid $base-light-2;
  }

  &__labels {
    position: absolute;
    top: 50%;
    right: 0;
    transform: translateY(-50%);
    display: flex;
    align-items: flex-end;
    z-index: 10;

    &:not(:last-child) {
      margin-right: $spacing;
    }

    .tooltip {
      margin-left: $spacing * 0.5;
    }
  }

  &__label {
    margin-bottom: 0;
    padding: $spacing * 0.5 $spacing * 2;
    border-radius: $spacing * 2;
    color: white;
    @include font-size(12px);
    font-weight: 400;
  }

  &__date {
    @include font-size(14px);
  }

  &__comment {
    @include font-size(14px);
    line-height: 20px;
    padding: $spacing * 2 $spacing * 4;
    background-color: $light-4;
    position: relative;
    border-radius: 20px;
    margin-bottom: $spacing * 2;

    &--line {
      &:before {
        content: "";
        position: absolute;
        left: -43px;
        top: -5px;
        bottom: 0;
        width: 1px;
      }
    }

    &--missing {
      @include font-size(12px);
      color: $gray-light;
      font-weight: 400;
      line-height: 17px;
      background-color: transparent;
      padding: 0;
    }
  }

  &__comment-icon {
    color: $gray-300;
    margin-right: $spacing;
  }

  &__reply {
    @include font-size(14px);
    background-color: $lime-light;
    padding: $spacing * 2 $spacing * 4;
    border-radius: 20px;
    margin-top: $spacing;
    margin-bottom: $spacing * 2;

    &--title {
      @include font-size(14px);
      font-weight: 600;
      line-height: 20px;
      margin-bottom: $spacing;
    }
  }

  &__name {
    font-weight: 600;
    @include font-size(15px);
    line-height: 18px;
    margin-bottom: 5px;
  }
  ::v-deep .v-text-field__details {
    margin-bottom: $spacing;
  }
}

.-ml-2 {
  margin-left: -5px;
}

.cursor-pointer {
  cursor: pointer;
}

.reply-info {
  max-width: 582px;
  width: 100%;
  flex: 0 0 40%;
  padding-left: $spacing * 4;
  position: relative;
}

.reply-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  margin: $spacing * -0.75 0;
  gap: $spacing;

  ::v-deep .v-btn {
    margin-top: $spacing * 0.75;
    margin-bottom: $spacing * 0.75;
  }
}
</style>
